import React, { useEffect, useRef } from 'react';
import { Button } from 'antd';
import { ArrowRightOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import './LinearHero.css';

/**
 * LinearHero — §4.1 of the /linear page.
 *
 * Vision-led. Typography-only. No inline image — the reader scrolls one screen
 * and THEN gets the product (§4.2 product hero). This is the Apple move.
 *
 * Top element is a co-brand lockup: [Linear logo] × [CellCog C mark].
 * Theme-adaptive Linear logo (linear.svg for light, linear-dark.svg for dark).
 */
const LinearHero = ({ ctaLabel, onConnect, onSeeHowItWorks, connecting }) => {
  const rootRef = useRef(null);
  const { isDarkMode } = useTheme();

  useEffect(() => {
    const el = rootRef.current;
    if (el) el.classList.add('linear-hero--mounted');
  }, []);

  // Linear logo: file naming is inverted — linear.svg has white fill
  // (for DARK backgrounds) and linear-dark.svg has dark fill (for LIGHT).
  const linearLogoSrc = isDarkMode
    ? '/assets/icons/linear.svg'
    : '/assets/icons/linear-dark.svg';

  // CellCog mark: same naming inversion rule as Linear.
  // cellcog-mark-dark.png = vibrant teal (reads on LIGHT backgrounds).
  // cellcog-mark-light.png = pale teal (reads on DARK backgrounds).
  const cellcogLogoSrc = isDarkMode
    ? '/assets/linear/cellcog-mark-light.png'
    : '/assets/linear/cellcog-mark-dark.png';

  return (
    <section className="linear-hero" ref={rootRef}>
      <div className="linear-hero__gradient" aria-hidden="true" />
      <div className="linear-hero__inner">
        {/* Co-brand lockup: Linear × CellCog.
            TWO-FACE RULE (adaptive shell, Aug 23): the shell pins isDarkMode
            true, so the JS-picked src is the DARK-face artwork there; each
            mark also renders a light-artwork twin that the adaptive CSS
            shows only on the light face (mkt-face-* classes). Outside the
            marketing shell the twins stay hidden and the JS pick follows
            the app theme as before. */}
        <div className="linear-hero__lockup" aria-label="Linear and CellCog">
          <img
            src={linearLogoSrc}
            alt="Linear"
            className="linear-hero__lockup-linear mkt-face-dark"
          />
          <img
            src="/assets/icons/linear-dark.svg"
            alt=""
            className="linear-hero__lockup-linear mkt-face-light"
          />
          <span className="linear-hero__lockup-x" aria-hidden="true">
            ×
          </span>
          <img
            src={cellcogLogoSrc}
            alt="CellCog"
            className="linear-hero__lockup-cellcog mkt-face-dark"
          />
          <img
            src="/assets/linear/cellcog-mark-dark.png"
            alt=""
            className="linear-hero__lockup-cellcog mkt-face-light"
          />
        </div>

        <h1 className="linear-hero__title display-font">
          The first agent in Linear
          <br className="linear-hero__title-break" />
          <span className="linear-hero__title-accent">that isn&rsquo;t a coder.</span>
        </h1>

        <p className="linear-hero__subtitle">
          CellCog lives inside Linear as your team&rsquo;s multimodal teammate —
          research, reports, presentations, images, video, and dashboards —
          delivered as native Linear agent activities.
        </p>

        <div className="linear-hero__ctas">
          <Button
            type="primary"
            size="large"
            className="linear-hero__cta-primary"
            onClick={onConnect}
            loading={connecting}
            data-msc-suppress
          >
            {ctaLabel}
            <ArrowRightOutlined />
          </Button>
          <Button
            size="large"
            className="linear-hero__cta-secondary"
            onClick={onSeeHowItWorks}
            icon={<ArrowDownOutlined />}
          >
            See how it works
          </Button>
        </div>

        <p className="linear-hero__trust">
          5-second setup · Pay per task with CellCog credits ·{' '}
          <Link to="/pricing">View pricing →</Link>
        </p>
      </div>
    </section>
  );
};

export default LinearHero;
